import { useDispatch, useSelector } from 'react-redux'
import './CertificateTypeList.scss'
import { type RootState } from '../../../Store/Store'
import { TextInput } from '../../../Components/TextInput/TextInput'
import { useEffect, useMemo, useState } from 'react'
import { useLocalization } from '../../../Hooks/useLocalization'
import { setIsCertificateTypesRequested } from '../../../Store/DataSlice'
import { Button } from '../../../Components/Button/Button'
import { setDestinationView } from '../../../Store/InterfaceSlice'
import { CertificateTypeTable } from '../CertificateTypeTable'
import { SingleSelect } from '../../../Components/SingleSelect/SingleSelect'
import { sortResultString } from '../../../Utils/Arrays'

const localizationSection = 'certificate-type'
export function CertificateTypeList() {
  const dispatch = useDispatch()
  const localization = useLocalization(localizationSection)

  const isCertificateTypesRequested = useSelector(
    (state: RootState) => state.data.isCertificateTypesRequested
  )
  useEffect(() => {
    if (!isCertificateTypesRequested)
      dispatch(setIsCertificateTypesRequested(true))
  }, [isCertificateTypesRequested, dispatch])

  const allCertificateTypes = useSelector(
    (state: RootState) => state.data.certificateTypes
  )

  const [filterName, setFilterName] = useState('')
  const [filterIsActive, setFilterIsActive] = useState<'yes' | 'no' | ''>('')
  const [filterNaceLimitation, setFilterNaceLimitation] = useState('')

  const filteredCertificateTypes = useMemo(() => {
    return allCertificateTypes
      .filter((c) => {
        if (
          filterName &&
          !c.name.toLowerCase().includes(filterName.toLowerCase())
        )
          return false
        if (filterIsActive !== '' && !c.isActive !== (filterIsActive === 'yes'))
          return false
        if (
          filterNaceLimitation &&
          !c.naceLimitation.some((nl) =>
            nl.toLowerCase().includes(filterNaceLimitation.toLowerCase())
          )
        )
          return false
        return true
      })
      .sort((a, b) =>
        a.isActive !== b.isActive
          ? (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0)
          : sortResultString(a.name, b.name)
      )
  }, [allCertificateTypes, filterIsActive, filterNaceLimitation, filterName])

  return (
    <div className="certificate-type-list">
      <div className="button-row">
        <Button
          importance={'primary'}
          text={localization.text(
            'add-certificate-type',
            'Voeg certificaat type toe'
          )}
          onClick={() =>
            dispatch(
              setDestinationView({
                destinationView: 'certificate-type-add',
              })
            )
          }
        />
      </div>
      <div className="filters">
        <div className="filter-field">
          <TextInput
            label={localization.text('filter-name-input-label', 'Naam')}
            helperText={localization.text(
              'filter-name-input-helper',
              'Geef (een deel van) de naam van het certificaat type in'
            )}
            value={filterName}
            onChange={setFilterName}
          />
        </div>
        <div className="filter-field">
          <SingleSelect
            label={localization.text(
              'filter-is-active-input-label',
              'Is actief'
            )}
            helperText={localization.text(
              'filter-is-active-input-helper',
              'Is het actief of niet'
            )}
            value={filterIsActive}
            onChange={(newValue) => {
              setFilterIsActive(
                newValue === 'yes' ? 'yes' : newValue === 'no' ? 'no' : ''
              )
            }}
            items={[
              {
                value: 'yes',
                label: localization.text('yes', 'is actief'),
              },
              {
                value: 'no',
                label: localization.text('no', 'is niet actief'),
              },
            ]}
          />
        </div>
        <div className="filter-field">
          <TextInput
            label={localization.text(
              'filter-nace-limitation-input-label',
              'NACE beperking'
            )}
            helperText={localization.text(
              'filter-nace-limitation-input-helper',
              'Geef (een deel van) de NACE beperking in'
            )}
            value={filterNaceLimitation}
            onChange={setFilterNaceLimitation}
          />
        </div>
      </div>
      <CertificateTypeTable
        certificateTypes={filteredCertificateTypes}
        isLoading={allCertificateTypes.length === 0}
      />
    </div>
  )
}
